import { Stack, TextField, Button } from '@mui/material'

export const MAX_PACKAGES = 5

// Compact field height shared by every weight/cost field in this row — MUI's
// own "small" size defaults to ~40px; this trims it to ~30px so a
// multi-package list doesn't dominate the Proposed Step's vertical space.
// Exported so SuggestionPanel.jsx's own sibling fields (Label deduction) in
// this same blue Disposition section match it exactly.
export const compactFieldSx = { '& .MuiInputBase-input': { py: 0.5 }, '& .MuiInputBase-root': { minHeight: 30 } }

// Shared "Pkg 1, Pkg 2, ..." weight-editor list (feature/return-label-multi-
// package) — used by both the AI Proposed Step path (SuggestionPanel.jsx,
// which also shows a per-package Customer/Our Cost breakdown alongside each
// weight field via renderExtra) and the Other Actions manual path
// (CreateReturnLabelModal.jsx, weight only, no cost estimate at all).
//
// Package 1 can never be removed. A hard cap of 5 disables "Add a Package"
// once reached. Adding a package does NOT redistribute package 1's own
// weight — package 1 keeps the full calculated total and the agent splits
// it by hand; a smarter split may come later, this is deliberate for now.
//
// packages: [{ weight }], weight a number or '' (an in-progress edit).
// onChange(nextPackages) — the caller owns the actual state (actionInputs
// on the AI path, local form state on the manual path).
export default function PackagesEditor({ packages, onChange, disabled = false, renderExtra = null }) {
  function updateWeight(index, raw) {
    const weight = raw === '' ? '' : Number(raw)
    onChange(packages.map((p, i) => (i === index ? { ...p, weight } : p)))
  }
  function addPackage() {
    if (packages.length >= MAX_PACKAGES) return
    onChange([...packages, { weight: 0 }])
  }
  function removePackage(index) {
    if (index === 0) return
    onChange(packages.filter((_, i) => i !== index))
  }

  return (
    <Stack spacing={0.75}>
      {packages.map((pkg, i) => (
        // renderExtra's own cost text sits INLINE, beside the weight field,
        // rather than on a wrapping line below it — one row per package
        // instead of two.
        <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            type="number"
            label={`Pkg ${i + 1} weight`}
            value={pkg.weight ?? ''}
            onChange={(ev) => updateWeight(i, ev.target.value)}
            disabled={disabled}
            sx={{ maxWidth: 160, ...compactFieldSx }}
          />
          {renderExtra ? renderExtra(i, pkg) : null}
          {i > 0 && (
            <Button size="small" color="error" variant="outlined" onClick={() => removePackage(i)} disabled={disabled} sx={{ py: 0.25, minHeight: 0 }}>
              Remove
            </Button>
          )}
        </Stack>
      ))}
      <Button
        size="small" variant="outlined" onClick={addPackage} disabled={disabled || packages.length >= MAX_PACKAGES}
        sx={{ alignSelf: 'flex-start', py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}
      >
        Add a Package
      </Button>
    </Stack>
  )
}
